import { getClassLevel } from '../../classIdentity.mjs';

// 2024 Circle of the Land (SRD 5.2.1). The land chosen after a Long Rest is saved at `classState.druid.land` as
// { id, chosenAtLongRest }. Circle Spells are always prepared through feature grants, with Druid slots and Wisdom, and count
// as Druid spells. Land's Aid is the server's landsAid.js. Natural Recovery's Short Rest slots run through spellRecovery.js.
export const LAND_SUBCLASS_ID = 'land';
export const CIRCLE_SPELLS_FEATURE_ID = 'druid:land:circle-spells';
export const LANDS_AID_FEATURE_ID = 'druid:land:lands-aid';
export const NATURAL_RECOVERY_FEATURE_ID = 'druid:land:natural-recovery';
export const NATURES_WARD_FEATURE_ID = 'druid:land:natures-ward';
export const NATURES_SANCTUARY_FEATURE_ID = 'druid:land:natures-sanctuary';
export const LAND_SPELL_GRANT_PREFIX = 'druid-land-';
export const LAND_FREE_GRANT_PREFIX = 'druid-land-free-';
export const LANDS_AID_RANGE_FEET = 60;
export const LANDS_AID_RADIUS_FEET = 10;
const LEVELS = Object.freeze({ circleSpells: 3, landsAid: 3, naturalRecovery: 6, naturesWard: 10, naturesSanctuary: 14 });

export const LANDS = Object.freeze({
  arid: Object.freeze({ id: 'arid', name: 'Arid', resistance: 'fire', spells: Object.freeze({ 3: ['blur', 'burning-hands', 'fire-bolt'], 5: ['fireball'], 7: ['blight'], 9: ['wall-of-stone'] }) }),
  polar: Object.freeze({ id: 'polar', name: 'Polar', resistance: 'cold', spells: Object.freeze({ 3: ['fog-cloud', 'hold-person', 'ray-of-frost'], 5: ['sleet-storm'], 7: ['ice-storm'], 9: ['cone-of-cold'] }) }),
  temperate: Object.freeze({ id: 'temperate', name: 'Temperate', resistance: 'lightning', spells: Object.freeze({ 3: ['misty-step', 'shocking-grasp', 'sleep'], 5: ['lightning-bolt'], 7: ['freedom-of-movement'], 9: ['tree-stride'] }) }),
  tropical: Object.freeze({ id: 'tropical', name: 'Tropical', resistance: 'poison', spells: Object.freeze({ 3: ['acid-splash', 'ray-of-sickness', 'web'], 5: ['stinking-cloud'], 7: ['polymorph'], 9: ['insect-plague'] }) }),
});

const isLand = character => {
  const state = character?.classState?.druid;
  const raw = state?.subclassId ?? state?.subclass?.id ?? (typeof state?.subclass === 'string' ? state.subclass : null);
  return String(raw || '').trim().toLowerCase() === LAND_SUBCLASS_ID;
};
export const hasLandFeature = (character, feature) => isLand(character) && getClassLevel(character, 'druid') >= (LEVELS[feature] ?? Infinity);

export const getLand = character => (hasLandFeature(character, 'circleSpells') ? LANDS[character?.classState?.druid?.land?.id] || null : null);
export const needsLand = character => hasLandFeature(character, 'circleSpells') && !getLand(character);
/** The land can change after each Long Rest; with none chosen yet, it can be chosen now. */
export const canChangeLand = character => hasLandFeature(character, 'circleSpells')
  && (!getLand(character) || (Number(character?.restState?.longCount) || 0) > (Number(character?.classState?.druid?.land?.chosenAtLongRest) || 0));
/** Circle Spells prepared now, for the Druid's level and land, lowest level first. */
export const getLandSpells = character => {
  const land = getLand(character);
  if (!land) return [];
  const level = getClassLevel(character, 'druid');
  return Object.entries(land.spells).filter(([gained]) => level >= Number(gained)).flatMap(([, spells]) => spells);
};
export const landSpellGrantId = spellId => `${LAND_SPELL_GRANT_PREFIX}${spellId}`;
export const naturalRecoveryGrantId = spellId => `${LAND_FREE_GRANT_PREFIX}${spellId}`;
export const naturalRecoverySpellForGrant = grantId => (String(grantId || '').startsWith(LAND_FREE_GRANT_PREFIX) ? String(grantId).slice(LAND_FREE_GRANT_PREFIX.length) : null);
export const landSpellForGrant = grantId => (String(grantId || '').startsWith(LAND_SPELL_GRANT_PREFIX) && !naturalRecoverySpellForGrant(grantId) ? String(grantId).slice(LAND_SPELL_GRANT_PREFIX.length) : null);

/** Land's Aid rolls 2d6, 3d6 at Druid 10 and 4d6 at 14, for its damage and again for its healing. */
export const landsAidDice = level => (level >= 14 ? 4 : level >= 10 ? 3 : 2);

const naturalCastSpent = character => Object.entries(character?.spellcastingState?.speciesFreeCasts || {})
  .some(([grantId, used]) => String(grantId).startsWith(LAND_FREE_GRANT_PREFIX) && Number(used) > 0);
/** Natural Recovery's free cast: one level 1+ Circle Spell, once per Long Rest, shared across them. */
export const canCastNaturalRecovery = character => hasLandFeature(character, 'naturalRecovery') && !naturalCastSpent(character);
/** Natural Recovery's Short Rest slots: half the Druid level, rounded up, in combined slot levels. */
export const naturalRecoveryBudget = level => Math.ceil(Number(level) / 2);

/** Nature's Ward's Resistance follows the land chosen now. */
export const natureWardResistance = character => (hasLandFeature(character, 'naturesWard') ? getLand(character)?.resistance || null : null);

const land = Object.freeze({
  id: LAND_SUBCLASS_ID, definitionId: 'druid:land', classId: 'druid', aliases: Object.freeze(['circle of the land']),
  name: 'Circle of the Land', selectionLevel: 3,
  description: 'Celebrate connection to the natural world.',
  features: Object.freeze([
    {
      id: CIRCLE_SPELLS_FEATURE_ID, name: 'Circle of the Land Spells', level: 3,
      description: 'Whenever you finish a Long Rest, choose one type of land: arid, polar, temperate, or tropical. You have that land’s spells for your Druid level and lower prepared.',
      presentation: { kind: 'passive' }, contracts: [],
    },
    {
      id: LANDS_AID_FEATURE_ID, name: 'Land’s Aid', level: 3,
      description: 'As a Magic action, you can expend a use of your Wild Shape and choose a point within 60 feet of yourself. Each creature of your choice in a 10-foot-radius Sphere there makes a Constitution save against your spell save DC, taking 2d6 Necrotic damage on a failed save or half as much on a success. One creature of your choice there regains 2d6 Hit Points. Both increase by 1d6 at Druid levels 10 and 14.',
      presentation: { kind: 'action' }, contracts: [],
    },
    {
      id: NATURAL_RECOVERY_FEATURE_ID, name: 'Natural Recovery', level: 6,
      description: 'You can cast one of the level 1+ spells prepared from your Circle Spells without a spell slot, once per Long Rest. When you finish a Short Rest, you can recover expended spell slots with a combined level up to half your Druid level (round up), none of them level 6+, once per Long Rest.',
      presentation: { kind: 'passive' }, contracts: [],
    },
    {
      id: NATURES_WARD_FEATURE_ID, name: 'Nature’s Ward', level: 10,
      description: 'You are immune to the Poisoned condition, and you have Resistance to a damage type from your current land: Fire for arid, Cold for polar, Lightning for temperate, Poison for tropical.',
      presentation: { kind: 'passive' }, contracts: [],
    },
    {
      id: NATURES_SANCTUARY_FEATURE_ID, name: 'Nature’s Sanctuary', level: 14,
      description: 'As a Magic action, you can expend a use of your Wild Shape and raise spectral trees and vines in a 15-foot Cube on the ground within 120 feet of yourself. For 1 minute, or until you have the Incapacitated condition or die, you and your allies have Half Cover in that area, and your allies gain the current Resistance of your Nature’s Ward there. As a Bonus Action, you can move the Cube up to 60 feet to ground within 120 feet of yourself.',
      presentation: { kind: 'action' }, contracts: [],
    },
  ]),
});

export default land;
